/**
 * The page registry and its budget — `/v1/admin/seo/*` (docs/05-admin/23 §3.4, backend 40 §4.6).
 *
 * Every public URL the site could render is a row here, and each sits in one of three tiers:
 * **prerendered** (a static file in the deploy), **edge** (rendered on first request and cached)
 * or **excluded** (not rendered, not in the sitemap). Cloudflare Pages caps the files in one
 * deploy, so the prerendered tier has a ceiling, and the meter at the top is that ceiling.
 *
 * ⛔ **The budget is the server's arithmetic, never the screen's.** A promote past the ceiling is
 * refused with `409 budget_exceeded`, and the refusal's sentence (it carries the headroom sums) is
 * shown verbatim in the dialog, which stays open. The screen never pre-judges a promote: the
 * static-asset estimate and the locked count live on the server, and a client that guessed would
 * one day wave through the flip that breaks a deploy.
 *
 * ⛔ **A manual tier change pins the row.** The nightly scoring job would otherwise undo it on its
 * next run. A pinned row must be unpinned, as its own audited act, before its tier can move again;
 * the server enforces that, and this screen says it before the operator tries.
 *
 * ⛔ **Every write takes a reason,** recorded in the audit log with the operator's name. The flips
 * listed under the meter are read from that log, so "who excluded this page and why" has an answer.
 */
import { useState } from 'react';
import { Button, Card, Icon, Kicker, StatusChip } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { DataTable, DetailPage } from '../components/data';
import { KV } from '../components/entities';
import { ConfirmAction, Consequences, FilterBar, ScopeAction, StatTile, REFUSED } from '../components/kit';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { useConsole } from '../app/console';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { errorMessage } from '../lib/api/client';
import {
  useRegenerateSitemaps,
  useRegistry,
  useSeoBudget,
  useSetBoost,
  useSetFamilyBoost,
  useSetTier,
  SEO_TAB_KEYS,
  type RegistryQuery,
  type SeoBudget,
  type SeoRegistryRow,
  type SeoTier,
} from '../lib/api/hooks/seo';
import { formatDate, formatDateTime } from '../lib/datetime';
import { REBALANCED, describeFlip } from './seoFlips';

export const PAGE_SIZE = 50;

export const TIERS: readonly SeoTier[] = ['prerendered', 'edge', 'excluded'];

export const TIER_TONE: Readonly<Record<SeoTier, 'success' | 'info' | 'neutral'>> = {
  prerendered: 'success',
  edge: 'info',
  excluded: 'neutral',
};

export const TIER_MEANS: Readonly<Record<SeoTier, string>> = {
  prerendered: 'A static file in every deploy — counts against the file budget',
  edge: 'Rendered on first request and cached at the edge — costs no deploy file',
  excluded: 'Not rendered and not in the sitemap — the URL answers 404 until it is moved back',
};

/** The verb on each tier's button, so the dialog title and the button never disagree. */
export const TIER_VERB: Readonly<Record<SeoTier, string>> = {
  prerendered: 'Prerender',
  edge: 'Move to edge',
  excluded: 'Exclude',
};

export function tierOf(row: SeoRegistryRow): SeoTier {
  return row.generation_tier as SeoTier;
}

/** The tab bar is the tier filter plus "pinned"; one translation into the server's query. */
export function registryQuery(tab: string, q: string, pageType: string, page: number): RegistryQuery {
  const params: RegistryQuery = { page, page_size: PAGE_SIZE };
  if ((TIERS as readonly string[]).includes(tab)) params.tier = tab as SeoTier;
  if (tab === 'pinned') params.locked = true;
  if (q.trim()) params.q = q.trim();
  if (pageType.trim()) params.page_type = pageType.trim();
  return params;
}

/** "Used / ceiling", and whether the meter should read as a warning (under 5% left). */
export function budgetLine(budget: SeoBudget): { used: number; warn: boolean; note: string } {
  const used = budget.prerendered + budget.static_asset_estimate;
  const warn = budget.headroom <= Math.ceil(budget.budget_max * 0.05);
  return {
    used,
    warn,
    note: `${budget.prerendered} pages + ~${budget.static_asset_estimate} static assets of ${budget.budget_max} files · ${budget.headroom} left`,
  };
}

/** A boost the server will take: a whole number from 0 to 100. Null for anything else. */
export function parseBoost(raw: string): number | null {
  if (!/^\d{1,3}$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return n <= 100 ? n : null;
}

type Dialog = { kind: 'tier'; tier: SeoTier } | { kind: 'pin' } | { kind: 'boost' } | { kind: 'family' } | { kind: 'sitemaps' };

export function SeoRegistry() {
  const { scope, toast } = useConsole();
  /* Tab, path search and page type live in the URL — the budget dashboard drills in by writing them. */
  const [q, setQ] = useUrlSearch('seo');
  const [tab, setTab] = useUrlTab('seo', SEO_TAB_KEYS, 'all');
  const [pageType, setPageType] = useUrlSearch('seo', 'type');
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [boost, setBoost] = useState('');
  const [familyType, setFamilyType] = useState('');

  const params = registryQuery(tab, q, pageType, page);
  const list = useRegistry(params);
  const budget = useSeoBudget();
  const setTier = useSetTier();
  const setRowBoost = useSetBoost();
  const setFamily = useSetFamilyBoost();
  const regenerate = useRegenerateSitemaps();

  const items = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const { focus, setFocus, open: focused, setOpen } = useQueue(items, { key: JSON.stringify(params) });
  /* As in Redirects: the queue holds WHICH row is open, the list holds what it says now. */
  const open = items.find((r) => r.id === focused?.id) ?? null;

  const onFilter = (fn: () => void) => {
    fn();
    setPage(1);
  };
  const close = () => {
    setDialog(null);
    setBoost('');
    setFamilyType('');
    setTier.reset();
    setRowBoost.reset();
    setFamily.reset();
  };
  const refusal = (error: unknown) => <Consequences tone="error" kicker={REFUSED} items={[errorMessage(error)]} />;

  const b = budget.data;
  const meter = b && budgetLine(b);
  /* Picked from the types the registry holds, not typed: a typo is a valid request that boosts nothing. */
  const familyRows = b?.composition ?? [];
  const familyPages = familyRows
    .filter((r) => r.page_type === familyType)
    .reduce((sum, r) => sum + r.prerendered + r.edge + r.excluded, 0);

  const familyDialog = (
    <ConfirmAction
      open={dialog?.kind === 'family'}
      title="Boost a whole page type?"
      phrase="BOOST"
      tone="warn"
      reason
      consequences={[
        familyType
          ? `Sets the strategic boost on all ${familyPages} ${familyType} pages to ${parseBoost(boost) ?? '…'}.`
          : `Sets the strategic boost on every page of one type to ${parseBoost(boost) ?? '…'}.`,
        'The boost is read by the next scoring run. It moves no tier by itself, and it never lifts a page past the budget.',
      ]}
      onCancel={close}
      onConfirm={(reason) => {
        const value = parseBoost(boost);
        if (value === null || !familyType) return;
        setFamily.mutate(
          { pageType: familyType, boost: value, reason },
          {
            onSuccess: (out) => {
              close();
              toast({ tone: 'success', message: `Boost set on ${out.changed} pages · read at the next scoring run` });
            },
          },
        );
      }}
      confirmLabel={setFamily.isPending ? 'Saving…' : 'Set boost'}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px', gap: 'var(--sp-md)' }}>
        <Select
          density="admin"
          label="Page type"
          value={familyType}
          onChange={setFamilyType}
          options={familyRows.map((r) => r.page_type)}
          placeholder="Pick a page type"
        />
        <Input density="admin" label="Boost (0–100)" value={boost} onChange={setBoost} placeholder="20" />
      </div>
      {setFamily.isError && refusal(setFamily.error)}
    </ConfirmAction>
  );

  const sitemapsDialog = (
    <ConfirmAction
      open={dialog?.kind === 'sitemaps'}
      title="Regenerate the sitemaps?"
      phrase="REGENERATE"
      tone="warn"
      reason
      consequences={[
        'Queues a rebuild of every sitemap file from the registry as it stands now.',
        'Excluded pages drop out and newly prerendered or edge pages appear. Search engines see it on their next fetch.',
      ]}
      onCancel={close}
      onConfirm={(reason) =>
        regenerate.mutate(reason, {
          onSuccess: (out) => {
            close();
            toast(
              out.queued
                ? { tone: 'success', message: `Sitemap rebuild queued${out.job_id ? ` · job ${out.job_id}` : ''}` }
                : { tone: 'error', message: 'The worker did not take the job — nothing was queued' },
            );
          },
          onError: (error) => toast({ tone: 'error', message: errorMessage(error) }),
        })
      }
      confirmLabel={regenerate.isPending ? 'Queueing…' : 'Regenerate'}
    />
  );

  if (open) {
    const tier = tierOf(open);
    const target = dialog?.kind === 'tier' ? dialog.tier : null;
    const tierDialog = target && (
      <ConfirmAction
        open
        title={`${TIER_VERB[target]} ${open.path}?`}
        phrase={target === 'excluded' ? 'EXCLUDE' : target === 'prerendered' ? 'PRERENDER' : 'EDGE'}
        tone={target === 'excluded' ? 'error' : 'warn'}
        reason
        consequences={[
          `${open.path} moves from ${tier} to ${target}. ${TIER_MEANS[target]}.`,
          'The row is pinned so the nightly scoring job cannot undo this. Unpin it later to hand it back.',
          target === 'prerendered'
            ? 'The server checks the budget now. If there is no headroom it refuses and says by how much.'
            : 'Takes effect at the next site build.',
        ]}
        onCancel={close}
        onConfirm={(reason) =>
          setTier.mutate(
            { id: open.id, tier: target, reason },
            {
              onSuccess: () => {
                close();
                toast({ tone: 'success', message: `Moved to ${target} and pinned · effective at the next build` });
              },
              /* ⛔ Stays open on a refusal: `budget_exceeded` carries the headroom sums and
                 `tier_locked` says to unpin first, and a toast would take either away. */
            },
          )
        }
        confirmLabel={setTier.isPending ? 'Saving…' : TIER_VERB[target]}
      >
        {setTier.isError && refusal(setTier.error)}
      </ConfirmAction>
    );

    const pinDialog = (
      <ConfirmAction
        open={dialog?.kind === 'pin'}
        title={open.tier_locked ? 'Unpin this page?' : 'Pin this page?'}
        phrase={open.tier_locked ? 'UNPIN' : 'PIN'}
        tone="warn"
        reason
        consequences={
          open.tier_locked
            ? ['The nightly scoring job may move this page again on its next run, by its score and the budget.']
            : [`${open.path} stays ${tier} until somebody unpins it. The scoring job will leave it alone.`]
        }
        onCancel={close}
        onConfirm={(reason) =>
          setTier.mutate(
            { id: open.id, locked: !open.tier_locked, reason },
            {
              onSuccess: () => {
                close();
                toast({ tone: 'success', message: open.tier_locked ? 'Unpinned' : 'Pinned' });
              },
            },
          )
        }
        confirmLabel={setTier.isPending ? 'Saving…' : open.tier_locked ? 'Unpin' : 'Pin'}
      >
        {setTier.isError && refusal(setTier.error)}
      </ConfirmAction>
    );

    const boostDialog = (
      <ConfirmAction
        open={dialog?.kind === 'boost'}
        title="Set this page's boost?"
        phrase="BOOST"
        tone="warn"
        reason
        consequences={[
          `Strategic boost ${open.strategic_boost} → ${parseBoost(boost) ?? '…'}.`,
          'Read by the next scoring run. It moves no tier by itself.',
        ]}
        onCancel={close}
        onConfirm={(reason) => {
          const value = parseBoost(boost);
          if (value === null) return;
          setRowBoost.mutate(
            { id: open.id, boost: value, reason },
            {
              onSuccess: () => {
                close();
                toast({ tone: 'success', message: 'Boost saved · read at the next scoring run' });
              },
            },
          );
        }}
        confirmLabel={setRowBoost.isPending ? 'Saving…' : 'Set boost'}
      >
        <Input
          density="admin"
          label="Boost (0–100)"
          value={boost}
          onChange={setBoost}
          placeholder={String(open.strategic_boost)}
          helper={boost && parseBoost(boost) === null ? 'A whole number from 0 to 100.' : undefined}
        />
        {setRowBoost.isError && refusal(setRowBoost.error)}
      </ConfirmAction>
    );

    return (
      <DetailPage
        title={open.path}
        code={`${open.page_type} · ${open.locale}`}
        status={
          <StatusChip tone={TIER_TONE[tier]} label={open.tier_locked ? `${tier} · pinned` : tier} />
        }
        onClose={() => setOpen(null)}
        audit={open.tier_changed_at ? `Tier last changed ${formatDateTime(open.tier_changed_at)}` : 'Tier never changed'}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
            {TIERS.filter((t) => t !== tier).map((t) => (
              <ScopeAction
                key={t}
                scope={scope}
                need="catalogue_content"
                size="sm"
                variant={t === 'excluded' ? 'destructive' : 'secondary'}
                onClick={() => setDialog({ kind: 'tier', tier: t })}
              >
                {TIER_VERB[t]}
              </ScopeAction>
            ))}
            <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setDialog({ kind: 'pin' })}>
              {open.tier_locked ? 'Unpin' : 'Pin'}
            </ScopeAction>
            <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setDialog({ kind: 'boost' })}>
              Boost
            </ScopeAction>
          </div>
        }
      >
        <KV
          rows={[
            ['Path', open.path],
            ['Tier', <StatusChip key="t" tone={TIER_TONE[tier]} label={tier} />],
            ['What that means', TIER_MEANS[tier]],
            ['Pinned', open.tier_locked ? 'Yes — the scoring job leaves it alone' : 'No — the scoring job may move it'],
            ['Priority score', String(open.priority_score)],
            ['Strategic boost', String(open.strategic_boost)],
            ['In sitemap', open.in_sitemap ? 'Yes' : 'No'],
            ['Last rendered', open.last_rendered_at ? formatDateTime(open.last_rendered_at) : 'never'],
          ]}
        />
        {open.tier_locked && (
          <Consequences
            tone="warn"
            items={['This page is pinned. Unpin it first to change its tier — the server refuses a tier change on a pinned row.']}
          />
        )}
        {tierDialog}
        {pinDialog}
        {boostDialog}
      </DetailPage>
    );
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-sm)',
          background: 'var(--c-sunk)',
          border: '1px solid var(--c-border)',
          borderRadius: 'var(--r-tile)',
          padding: 'var(--sp-md) var(--sp-lg)',
          fontSize: 'var(--fs-admin-ui)',
          color: 'var(--c-ink2)',
          flexWrap: 'wrap',
        }}
      >
        <Icon name="globe" size={16} />
        <span style={{ flex: 1, minWidth: 280 }}>
          The nightly scoring job sorts pages into tiers by score and the file budget. A manual change
          pins the page so the job leaves it alone; every change takes a reason and is audited.
        </span>
        <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setDialog({ kind: 'family' })}>
          Boost a page type
        </ScopeAction>
        <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setDialog({ kind: 'sitemaps' })}>
          Regenerate sitemaps
        </ScopeAction>
      </div>

      {budget.isError && <ErrorState title="Could not read the budget" cause={errorMessage(budget.error)} />}
      {b && meter && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--sp-md)' }}>
            <StatTile label="Deploy files" value={`${meter.used} / ${b.budget_max}`} note={meter.note} tone={meter.warn ? 'error' : 'default'} />
            <StatTile label="Prerendered" value={b.prerendered} note={`${b.prerendered_locked} pinned`} />
            <StatTile label="Edge" value={b.edge} note="rendered on first request" />
            <StatTile label="Excluded" value={b.excluded} note="not rendered, not in the sitemap" />
          </div>
          {b.last_build ? (
            <Card variant="flat" padding="var(--sp-md) var(--sp-lg)">
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: b.last_build.mismatched || b.last_build.over_budget ? 'var(--c-error-text)' : 'var(--c-ink2)' }}>
                Last build {formatDateTime(b.last_build.finished_at)}: {b.last_build.page_count} pages,{' '}
                {b.last_build.file_count} files
                {b.last_build.over_budget && ' · over budget'}
                {b.last_build.mismatched && ` · ${b.last_build.drift} files differ from the registry`}
              </span>
            </Card>
          ) : (
            <Card variant="flat" padding="var(--sp-md) var(--sp-lg)">
              <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink3)' }}>
                No site build has reported a manifest yet, so the file count above is the registry's, not a deploy's.
              </span>
            </Card>
          )}
          {b.flips.length > 0 && (
            <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
              <Kicker>Recent tier changes</Kicker>
              <DataTable
                density="compact"
                columns={[
                  { key: 'when', label: 'When' },
                  { key: 'who', label: 'By' },
                  { key: 'what', label: 'Change' },
                  { key: 'why', label: 'Reason' },
                ]}
                rows={b.flips.map((f) => ({
                  id: f.id,
                  when: formatDateTime(f.created_at),
                  who: f.actor_admin_id ? `admin #${f.actor_admin_id}` : f.actor_type,
                  // A nightly run records what it moved, not one page's tier; describeFlip reads both.
                  what: f.action === REBALANCED ? `nightly run · ${describeFlip(f)}` : describeFlip(f),
                  why: f.reason || <span style={{ color: 'var(--c-ink3)' }}>—</span>,
                }))}
              />
            </div>
          )}
        </>
      )}

      <FilterBar
        q={q}
        onQ={(v) => onFilter(() => setQ(v))}
        placeholder="Path contains — press /"
        tab={tab}
        onTab={(t) => onFilter(() => setTab(t))}
        tabs={[
          { key: 'all', label: 'All' },
          { key: 'prerendered', label: 'Prerendered' },
          { key: 'edge', label: 'Edge' },
          { key: 'excluded', label: 'Excluded' },
          { key: 'pinned', label: 'Pinned' },
        ]}
        extra={
          <div style={{ width: 170 }}>
            <Input density="admin" value={pageType} onChange={(v) => onFilter(() => setPageType(v))} placeholder="Page type" />
          </div>
        }
      />

      {list.isPending && <LoadingState message="Reading the page registry…" />}
      {list.isError && <ErrorState title="Could not read the page registry" cause={errorMessage(list.error)} />}
      {list.isSuccess && items.length === 0 && (
        <Card variant="flat" padding="var(--sp-xxl)">
          <EmptyState
            icon="globe"
            title="No page matches this filter"
            body="The registry is filled by the nightly scoring job from the catalogue, cities, temples and CMS pages. An empty registry means the job has not run, not that the site has no pages."
          />
        </Card>
      )}
      {list.isSuccess && items.length > 0 && (
        <DataTable
          density="comfortable"
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(row) => setOpen(items.find((r) => r.id === row.id)!)}
          footer={
            <>
              <span>
                Page {page} of {pages} · {total} pages in this filter
              </span>
              <span style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
                <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </span>
              <span>{QUEUE_HINT}</span>
            </>
          }
          columns={[
            { key: 'path', label: 'Path', mono: true },
            { key: 'type', label: 'Page type' },
            { key: 'tier', label: 'Tier' },
            { key: 'score', label: 'Score', align: 'right' },
            { key: 'boost', label: 'Boost', align: 'right' },
            { key: 'rendered', label: 'Last rendered' },
          ]}
          rows={items.map((r) => ({
            id: r.id,
            path: r.path,
            type: r.page_type,
            tier: <StatusChip tone={TIER_TONE[tierOf(r)]} label={r.tier_locked ? `${r.generation_tier} · pinned` : r.generation_tier} />,
            score: r.priority_score.toFixed(2),
            boost: r.strategic_boost,
            rendered: r.last_rendered_at ? formatDate(r.last_rendered_at) : <span style={{ color: 'var(--c-ink3)' }}>never</span>,
          }))}
        />
      )}

      {familyDialog}
      {sitemapsDialog}
    </>
  );
}
